
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { format } from 'date-fns';
import frenchLocale from 'date-fns/locale/fr';
import { formatCurrencyByProject } from '@t/format-currency';
import { vxm } from '@/store';

@Component({
    components: {}
})

export default class ProvisionalAdvancementTable extends Vue {
  @Prop({ default: () => [] }) readonly data!: any[];
  @Prop({ default: () => [] }) readonly periods!: any[];
  @Prop({ required: true, default: false }) isReadOnly!: boolean;
  months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  formatedModificationDate: string = '';

  private async mounted() {
      this.formatedModificationDate = this.getLastModificationDate();
  }

  update() {
      this.$emit('update-all', this.data);
  }

  private getForecastValue(row: any, periode: string): string {
      if (row.status.code == 'BLC') {
          return row.advancement + '%';
      }

      const [monthStr, yearStr] = periode.split('_');
      const month = parseInt(monthStr);
      const year = parseInt(yearStr);

      const forecast = row.progressForecasts?.find(
          (f: any) => f.month === month && f.year === year
      );

      return forecast?.advancement !== undefined ? forecast.advancement + '%' : '';
  }

  private onAdvancementInput(row: any, periode: string, event: Event): void {
      const target = event.target as HTMLInputElement;
      const inputValue = target.value;

      const [monthStr, yearStr] = periode.split('_');
      const month = parseInt(monthStr);
      const year = parseInt(yearStr);

      const numericValue = parseFloat(inputValue.replace('%', '').replace(',', '.'));

      if (!row.progressForecasts) {
          row.progressForecasts = [];
      }

      let forecast = row.progressForecasts.find(
          (f: any) => f.month === month && f.year === year
      );

      if (!forecast) {
          forecast = { month, year, advancement: numericValue, value: 0 };
          row.progressForecasts.push(forecast);
      } else {
          forecast.advancement = numericValue;
      }

      const ordered = [...row.progressForecasts].sort((a, b) => {
          if (a.contractLineId !== b.contractLineId) {
              return a.contractLineId - b.contractLineId;
          } else if (a.year !== b.year) {
              return a.year - b.year;
          } else {
              return a.month - b.month;
          }
      });

      ordered.forEach(element => {
          if (ordered.findIndex(el => el.month == element.month && el.year == element.year) > 0) {
              const previous = ordered[ordered.findIndex(el => el.month == element.month && el.year == element.year) - 1];
              element.value = (row.regroupingTotal * element.advancement / 100) - (row.regroupingTotal * previous.advancement / 100); 
          } else {
              const current = ordered[ordered.findIndex(el => el.month == element.month && el.year == element.year)];
              element.value = (row.regroupingTotal * current.advancement / 100) - (row.regroupingTotal * row.advancement / 100);
          }  
      });
  }
  
  private getFormattedMonth(month: string, year: string): string {
      var monthStr = this.months[parseInt(month) - 1];    
      return `${monthStr} ${year}`;
  }

  private formatDate(date : Date, formatDate: string): string {
      const dateDefault = new Date('0001-01-01T00:00:00.000Z');
      if (date.getTime() == dateDefault.getTime()) {
          return '-';
      }

      return format(new Date(date), formatDate, { locale: frenchLocale });
  }

  private getSumFees() {
      return this.numberFormatter(this.data.map(_ => _.regroupingTotal).reduce((a, b) => a + b, 0));
  }

  private numberFormatter(value: number) {
      const truncated = Math.trunc(value);
      return formatCurrencyByProject(truncated, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  private sumAdvancement(key: number): string {
      var period = this.periods[key];

      var month = period ? parseInt(period.split('_')[0]) : null;
      var year = period ? parseInt(period.split('_')[1]) : null;
      let totalWeighted = 0;
      let totalRegrouping = 0;

      this.data.forEach(project => {
          const regroupingTotal = project.regroupingTotal;

          const filteredForecasts = project.progressForecasts.filter(forecast =>
              forecast.month === month && forecast.year === year
          );

          filteredForecasts.forEach(forecast => {
              totalWeighted += forecast.advancement * regroupingTotal;
              totalRegrouping += regroupingTotal;
          });
      });

      if (totalRegrouping === 0) return '0';

      return (totalWeighted / totalRegrouping).toFixed(2) + '%';
  }

  private sumDelta(key: number): string {
      var period = this.periods[key];
      if (period) {
          var month = parseInt(period.split('_')[0]);
          var year = parseInt(period.split('_')[1]);
          return this.numberFormatter(this.data
              .flatMap(item =>
                  item.progressForecasts?.filter(pf => pf.month == month && pf.year == year) || []
              )
              .map(pf => pf.value)
              .reduce((a, b) => a + b, 0));
      }
      return '';
  }

  isErrorAdvancement(row: any, periode: string) {
      const inputValue = this.getForecastValue(row, periode);
      const [monthStr, yearStr] = periode.split('_');
      const month = parseInt(monthStr);
      const year = parseInt(yearStr);

      const ordered = [...row.progressForecasts].sort((a, b) => {
          if (a.contractLineId !== b.contractLineId) {
              return a.contractLineId - b.contractLineId;
          } else if (a.year !== b.year) {
              return a.year - b.year;
          } else {
              return a.month - b.month;
          }
      });

      const currentIndex = ordered.findIndex(el => el.month == month && el.year == year);
      if (currentIndex > 0) {
          const previous = ordered[currentIndex - 1];
          if (previous.advancement > parseFloat(inputValue.replace('%', ''))) {
              return true;
          }
      } else if (currentIndex == 0) {
          if (row.advancement > parseFloat(inputValue.replace('%', ''))) {
              return true;
          }
      }
      return false;
  }

  getLastModificationDate(): string {
      let latestModifiedDate = new Date(0);

      this.data.forEach(project => {
          project.progressForecasts.forEach(forecast => {
              const modifiedDate = new Date(forecast.modifiedOn);
              if (!latestModifiedDate || modifiedDate > latestModifiedDate) {
                  latestModifiedDate = modifiedDate;
              }
          });
      });
      return this.formatDate(latestModifiedDate, 'dd/MM/yy  HH:mm:SS');
  }

  get currentProject() {
      return vxm.project.dropdownProject;
  }

  private formatProjectLabel(row, periode, currentProject) {
      this.numberFormatter(row.progressForecasts?.find(_ => 
          _.month == parseInt(periode.split('_')[0]) &&
                _.year == parseInt(periode.split('_')[1]))?.value) || 
                '0 ' + currentProject?.entity.currency?.symbol;
  }
}
